import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { cn, parseServiceTypeValues, toggleServiceTypeValue } from '../lib/utils';
import { format } from 'date-fns';
import {
    Users,
    Building2,
    RefreshCw,
    Settings,
    Search,
    Plus,
    Edit2,
    Loader2,
    Check,
    X,
    UserCheck,
    Link2,
    Link2Off,
    ExternalLink,
    Database,
    AlertTriangle,
    Merge,
} from 'lucide-react';
import type { ApiResponse, User, Client, ClientAccount, ClientAccountSyncStatus } from '../types';
import { config } from '../config';

type AdminTab = 'users' | 'clients' | 'client-portal' | 'sync' | 'settings';

export function Admin() {
    const [activeTab, setActiveTab] = useState<AdminTab>('users');

    const tabs = [
        { id: 'users' as AdminTab, label: 'Utilisateurs', icon: Users },
        { id: 'clients' as AdminTab, label: 'Clients', icon: Building2 },
        { id: 'client-portal' as AdminTab, label: 'Portail Client', icon: UserCheck },
        { id: 'sync' as AdminTab, label: 'Synchronisation', icon: RefreshCw },
        { id: 'settings' as AdminTab, label: 'Configuration', icon: Settings },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-[var(--color-text)]">Administration</h1>
                <p className="text-[var(--color-text-secondary)]">Gérez les utilisateurs, clients et paramètres</p>
            </div>

            {/* Tabs */}
            <div className="border-b border-[var(--color-border)] overflow-x-auto">
                <nav className="flex gap-2 sm:gap-4 min-w-max">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={cn(
                                'flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                                activeTab === id
                                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
                            )}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{label}</span>
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab content */}
            {activeTab === 'users' && <UsersTab />}
            {activeTab === 'clients' && <ClientsTab />}
            {activeTab === 'client-portal' && <ClientPortalTab />}
            {activeTab === 'sync' && <SyncTab />}
            {activeTab === 'settings' && <SettingsTab />}
        </div>
    );
}

// Users Tab
function UsersTab() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [editingUser, setEditingUser] = useState<User | null>(null);

    const { data: usersData, isLoading } = useQuery({
        queryKey: ['admin', 'users'],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/users'),
    });

    const users = usersData?.data || [];

    const filteredUsers = users.filter((user) => {
        const searchLower = search.toLowerCase();
        return (
            user.displayName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
        );
    });

    const updateUserMutation = useMutation({
        mutationFn: ({ userId, data }: { userId: string; data: Partial<User> }) =>
            api.put(`/api/users/${userId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
            setEditingUser(null);
        },
    });

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un utilisateur..."
                        className="input pl-10"
                    />
                </div>
                <span className="text-sm text-[var(--color-text-secondary)]">
                    {filteredUsers.length} utilisateur(s)
                </span>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : (
                <div className="card overflow-hidden -m-6 mt-0">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px]">
                            <thead className="bg-[var(--color-bg)]">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Utilisateur
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Rôle
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Manager
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--color-border)]">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-[var(--color-bg)]">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                                                    <span className="text-[var(--color-primary)] text-sm font-medium">
                                                        {user.displayName
                                                            .split(' ')
                                                            .map((n: string) => n[0])
                                                            .join('')
                                                            .toUpperCase()
                                                            .slice(0, 2)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[var(--color-text)]">{user.displayName}</p>
                                                    <p className="text-sm text-[var(--color-text-secondary)]">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <RoleBadge role={user.role} />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
                                            {user.managerName || '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={cn(
                                                    'px-2 py-0.5 rounded-full text-xs font-medium',
                                                    user.active
                                                        ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
                                                        : 'bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]'
                                                )}
                                            >
                                                {user.active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setEditingUser(user)}
                                                className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    users={users}
                    onClose={() => setEditingUser(null)}
                    onSave={(data) =>
                        updateUserMutation.mutate({ userId: editingUser.id, data })
                    }
                    isLoading={updateUserMutation.isPending}
                />
            )}
        </div>
    );
}

function RoleBadge({ role }: { role: string }) {
    const config = {
        admin: { label: 'Admin', classes: 'badge-purple' },
        manager: { label: 'Manager', classes: 'badge-primary' },
        billing: { label: 'Facturation', classes: 'badge-warning' },
        collaborator: { label: 'Collaborateur', classes: 'badge-default' },
    }[role] || { label: role, classes: 'badge-default' };

    return (
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', config.classes)}>
            {config.label}
        </span>
    );
}

interface EditUserModalProps {
    user: User;
    users: User[];
    onClose: () => void;
    onSave: (data: Partial<User>) => void;
    isLoading: boolean;
}

function EditUserModal({ user, users, onClose, onSave, isLoading }: EditUserModalProps) {
    const [formData, setFormData] = useState({
        role: user.role as 'collaborator' | 'manager' | 'admin' | 'billing',
        managerId: user.managerId || '',
        active: user.active,
        teamReportingAccess: user.teamReportingAccess ?? false,
    });

    const managers = users.filter((u) => u.role === 'manager' || u.role === 'admin');

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-md">
                <div className="p-6 border-b border-[var(--color-border)]">
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                        Modifier {user.displayName}
                    </h2>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Rôle
                        </label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value as 'collaborator' | 'manager' | 'admin' | 'billing' })}
                            className="input"
                        >
                            <option value="collaborator">Collaborateur</option>
                            <option value="manager">Manager</option>
                            <option value="billing">Facturation</option>
                            <option value="admin">Administrateur</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Manager
                        </label>
                        <select
                            value={formData.managerId}
                            onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
                            className="input"
                        >
                            <option value="">Aucun manager</option>
                            {managers.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[var(--color-text-secondary)]">Actif</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={formData.active}
                            onClick={() => setFormData({ ...formData, active: !formData.active })}
                            className={cn(
                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                                formData.active ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-bg-hover)]'
                            )}
                        >
                            <span
                                className={cn(
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
                                    formData.active ? 'translate-x-5' : 'translate-x-0'
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[var(--color-text-secondary)]">Accès reporting équipe</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={formData.teamReportingAccess}
                            onClick={() => setFormData({ ...formData, teamReportingAccess: !formData.teamReportingAccess })}
                            className={cn(
                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                                formData.teamReportingAccess ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-bg-hover)]'
                            )}
                        >
                            <span
                                className={cn(
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
                                    formData.teamReportingAccess ? 'translate-x-5' : 'translate-x-0'
                                )}
                            />
                        </button>
                    </div>
                </div>

                <div className="p-6 border-t border-[var(--color-border)] flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="btn-secondary"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(formData)}
                        disabled={isLoading}
                        className="btn-primary"
                    >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

// Clients Tab
function ClientsTab() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);

    const { data: clientsData, isLoading } = useQuery({
        queryKey: ['admin', 'clients'],
        queryFn: () => api.get<ApiResponse<Client[]>>('/api/clients'),
    });

    const clients = clientsData?.data || [];

    const createMutation = useMutation({
        mutationFn: (data: Partial<Client>) => api.post('/api/clients', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
            setShowForm(false);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ clientId, data }: { clientId: string; data: Partial<Client> }) =>
            api.put(`/api/clients/${clientId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
            setEditingClient(null);
        },
    });

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    onClick={() => setShowForm(true)}
                    className="btn-primary"
                >
                    <Plus className="w-4 h-4" />
                    Nouveau client
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : clients.length === 0 ? (
                <div className="text-center py-12 card">
                    <Building2 className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
                    <p className="text-[var(--color-text-secondary)]">Aucun client</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clients.map((client) => (
                        <div key={client.id} className="card">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-medium text-[var(--color-text)]">{client.name}</h3>
                                        {/* Même gabarit que la pastille des projets internes : c'est
                                            d'ici que vient leur marquage. */}
                                        {client.isInternal && (
                                            <span
                                                className="px-2 py-0.5 bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs rounded"
                                                title="Client interne : ses projets sont marqués « Interne »"
                                            >
                                                Interne
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-[var(--color-text-secondary)]">{client.code}</p>
                                </div>
                                <button
                                    onClick={() => setEditingClient(client)}
                                    className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            </div>
                            {client.contactEmail && (
                                <p className="text-sm text-[var(--color-text-secondary)] mt-2">{client.contactEmail}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Client Form Modal */}
            {(showForm || editingClient) && (
                <ClientFormModal
                    client={editingClient}
                    onClose={() => {
                        setShowForm(false);
                        setEditingClient(null);
                    }}
                    onSave={(data) => {
                        if (editingClient) {
                            updateMutation.mutate({ clientId: editingClient.id, data });
                        } else {
                            createMutation.mutate(data);
                        }
                    }}
                    isLoading={createMutation.isPending || updateMutation.isPending}
                />
            )}
        </div>
    );
}

interface ClientFormModalProps {
    client: Client | null;
    onClose: () => void;
    onSave: (data: Partial<Client>) => void;
    isLoading: boolean;
}

function ClientFormModal({ client, onClose, onSave, isLoading }: ClientFormModalProps) {
    const [formData, setFormData] = useState({
        name: client?.name || '',
        code: client?.code || '',
        contactEmail: client?.contactEmail || '',
        billingEmail: client?.billingEmail || '',
        // `?? false` et non `|| undefined` : Toggle attend un booléen strict, et un
        // indéfini le rendrait non contrôlé, donc insensible au clic.
        isInternal: client?.isInternal ?? false,
    });

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-md">
                <div className="p-6 border-b border-[var(--color-border)]">
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                        {client ? 'Modifier le client' : 'Nouveau client'}
                    </h2>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Nom *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Code *
                        </label>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                            className="input"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Email contact
                        </label>
                        <input
                            type="email"
                            value={formData.contactEmail}
                            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Email facturation
                        </label>
                        <input
                            type="email"
                            value={formData.billingEmail}
                            onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                            className="input"
                        />
                    </div>

                    <div className="pt-2 border-t border-[var(--color-border)]">
                        <Toggle
                            checked={formData.isInternal}
                            onChange={(value) => setFormData({ ...formData, isInternal: value })}
                            label="Client interne"
                            description="L'entreprise elle-même. Ses projets portent la pastille « Interne », y compris ceux qui arrivent des synchronisations."
                        />
                    </div>
                </div>

                <div className="p-6 border-t border-[var(--color-border)] flex justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary">
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(formData)}
                        disabled={isLoading || !formData.name || !formData.code}
                        className="btn-primary"
                    >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

// Client Portal Tab - Gestion des comptes clients pour le portail reporting
function ClientPortalTab() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'linked' | 'unlinked'>('all');
    const [associatingAccount, setAssociatingAccount] = useState<ClientAccount | null>(null);

    // Fetch client accounts
    const { data: accountsData, isLoading: accountsLoading } = useQuery({
        queryKey: ['admin', 'client-accounts'],
        queryFn: () => api.get<ApiResponse<ClientAccount[]>>('/api/admin/client-accounts'),
    });

    // Fetch clients for association dropdown
    const { data: clientsData } = useQuery({
        queryKey: ['admin', 'clients'],
        queryFn: () => api.get<ApiResponse<Client[]>>('/api/clients'),
    });

    // Fetch sync status
    const { data: syncStatusData } = useQuery({
        queryKey: ['admin', 'client-accounts', 'sync-status'],
        queryFn: () => api.get<ApiResponse<ClientAccountSyncStatus>>('/api/admin/client-accounts/sync/status'),
    });

    const accounts = accountsData?.data || [];
    const clients = clientsData?.data || [];
    const syncStatus = syncStatusData?.data;

    // Filter accounts
    const filteredAccounts = accounts.filter((account) => {
        const searchLower = search.toLowerCase();
        const matchesSearch =
            account.displayName.toLowerCase().includes(searchLower) ||
            account.email.toLowerCase().includes(searchLower) ||
            (account.companyName?.toLowerCase().includes(searchLower) ?? false);

        const matchesFilter =
            filterStatus === 'all' ||
            (filterStatus === 'linked' && account.clientId) ||
            (filterStatus === 'unlinked' && !account.clientId);

        return matchesSearch && matchesFilter;
    });

    // Sync mutation
    const syncMutation = useMutation({
        mutationFn: () => api.post('/api/admin/client-accounts/sync'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'client-accounts'] });
        },
    });

    // Associate mutation
    const associateMutation = useMutation({
        mutationFn: ({ accountId, clientId }: { accountId: string; clientId: string | null }) =>
            api.put(`/api/admin/client-accounts/${accountId}/associate`, { clientId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'client-accounts'] });
            setAssociatingAccount(null);
        },
    });

    // Toggle active mutation
    const toggleActiveMutation = useMutation({
        mutationFn: (accountId: string) =>
            api.put(`/api/admin/client-accounts/${accountId}/toggle-active`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'client-accounts'] });
        },
    });

    const linkedCount = accounts.filter((a) => a.clientId).length;
    const unlinkedCount = accounts.filter((a) => !a.clientId).length;

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Total comptes</div>
                    <div className="text-2xl font-bold text-[var(--color-text)]">{accounts.length}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Associés</div>
                    <div className="text-2xl font-bold text-[var(--color-success)]">{linkedCount}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Non associés</div>
                    <div className="text-2xl font-bold text-[var(--color-warning)]">{unlinkedCount}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Dernière sync</div>
                    <div className="text-lg font-medium text-[var(--color-text)]">
                        {syncStatus?.lastSyncAt
                            ? format(new Date(syncStatus.lastSyncAt), 'dd/MM HH:mm')
                            : 'Jamais'}
                    </div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input pl-9 w-full sm:w-64"
                        />
                    </div>

                    {/* Filter */}
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as 'all' | 'linked' | 'unlinked')}
                        className="input"
                    >
                        <option value="all">Tous</option>
                        <option value="linked">Associés</option>
                        <option value="unlinked">Non associés</option>
                    </select>
                </div>

                {/* Sync Button */}
                <button
                    onClick={() => syncMutation.mutate()}
                    disabled={syncMutation.isPending}
                    className="btn-primary flex items-center gap-2"
                >
                    <RefreshCw className={cn('w-4 h-4', syncMutation.isPending && 'animate-spin')} />
                    Synchroniser depuis Entra ID
                </button>
            </div>

            {/* Info message */}
            <div className="bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <ExternalLink className="w-5 h-5 text-[var(--color-primary)] mt-0.5" />
                    <div>
                        <p className="text-sm text-[var(--color-text)]">
                            <strong>Portail Client :</strong> Les comptes listés ici proviennent de l'application entreprise Entra ID dédiée aux clients.
                            Associez chaque compte à un client CRA pour leur donner accès au reporting de leurs projets.
                        </p>
                        {config.reportingUrl && (
                            <a
                                href={config.reportingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[var(--color-primary)] hover:underline mt-1 inline-block"
                            >
                                Accéder au portail client →
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Accounts Table */}
            <div className="card !p-0 overflow-hidden">
                {accountsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
                    </div>
                ) : filteredAccounts.length === 0 ? (
                    <div className="text-center py-12 text-[var(--color-text-secondary)]">
                        {search || filterStatus !== 'all'
                            ? 'Aucun compte ne correspond aux critères'
                            : 'Aucun compte client. Lancez une synchronisation depuis Entra ID.'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-secondary)]">
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Utilisateur
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Email
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Société
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Client associé
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Statut
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Dernière connexion
                                    </th>
                                    <th className="text-right p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccounts.map((account) => (
                                    <tr
                                        key={account.id}
                                        className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-secondary)]/50"
                                    >
                                        <td className="p-4">
                                            <div className="font-medium text-[var(--color-text)]">
                                                {account.displayName}
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.email}
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.companyName || '-'}
                                        </td>
                                        <td className="p-4">
                                            {associatingAccount?.id === account.id ? (
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        className="input text-sm py-1"
                                                        defaultValue={account.clientId || ''}
                                                        onChange={(e) => {
                                                            associateMutation.mutate({
                                                                accountId: account.id,
                                                                clientId: e.target.value || null,
                                                            });
                                                        }}
                                                    >
                                                        <option value="">-- Aucun --</option>
                                                        {clients.map((client) => (
                                                            <option key={client.id} value={client.id}>
                                                                {client.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={() => setAssociatingAccount(null)}
                                                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : account.clientId ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-success)]/10 text-[var(--color-success)]">
                                                    <Link2 className="w-3 h-3" />
                                                    {account.clientName}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-warning)]/10 text-[var(--color-warning)]">
                                                    <Link2Off className="w-3 h-3" />
                                                    Non associé
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={cn(
                                                    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                                                    account.active
                                                        ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                                                        : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                                                )}
                                            >
                                                {account.active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.lastLogin
                                                ? format(new Date(account.lastLogin), 'dd/MM/yyyy HH:mm')
                                                : 'Jamais'}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => setAssociatingAccount(account)}
                                                    className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 rounded-lg transition-colors"
                                                    title="Associer à un client"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => toggleActiveMutation.mutate(account.id)}
                                                    disabled={toggleActiveMutation.isPending}
                                                    className={cn(
                                                        'p-2 rounded-lg transition-colors',
                                                        account.active
                                                            ? 'text-[var(--color-error)] hover:bg-[var(--color-error)]/10'
                                                            : 'text-[var(--color-success)] hover:bg-[var(--color-success)]/10'
                                                    )}
                                                    title={account.active ? 'Désactiver' : 'Activer'}
                                                >
                                                    {account.active ? (
                                                        <X className="w-4 h-4" />
                                                    ) : (
                                                        <Check className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

interface SchedulerState {
    fullSync: {
        running: boolean;
        cronExpression: string;
        lastRun: string | null;
        nextRun: string | null;
    };
    dynamics: {
        scheduled: boolean;
        cronExpression: string;
        nextRun: string | null;
        lastRun: string | null;
        inProgress: boolean;
        lastDurationMs: number | null;
        lastResult: { synced: number; created: number; updated: number; errors: number } | null;
        lastError: string | null;
        lastSkipped: string | null;
    };
}

/**
 * Cadence en français, pour les expressions que le serveur produit lui-même
 * (« toutes les N minutes », « toutes les heures »). Toute autre expression est affichée
 * telle quelle plutôt que traduite de travers.
 */
function describeCron(expression: string): string {
    const everyMinutes = expression.match(/^\*\/(\d+) \* \* \* \*$/);
    if (everyMinutes) return `toutes les ${everyMinutes[1]} minutes`;
    if (expression === '0 * * * *') return 'toutes les heures';
    return `selon « ${expression} »`;
}

// Sync Tab
function SyncTab() {
    const api = useApi();
    const queryClient = useQueryClient();

    interface SyncLog {
        id: string;
        syncType: string;
        status: string;
        message: string;
        itemsSynced: number;
        startedAt: string;
        completedAt: string;
    }

    const { data: logsData, isLoading } = useQuery({
        queryKey: ['admin', 'sync-logs'],
        queryFn: () => api.get<ApiResponse<SyncLog[]>>('/api/sync/logs'),
    });

    // Les deux sources se coupent depuis l'onglet Configuration : on lit les réglages
    // pour ne pas proposer un bouton qui répondrait « désactivée ».
    const { data: settingsData } = useQuery({
        queryKey: ['admin', 'settings'],
        queryFn: () => api.get<ApiResponse<AppSettings>>('/api/settings'),
    });
    const sharepointEnabled = settingsData?.data?.sharepointSyncEnabled ?? true;
    const dynamicsEnabled = settingsData?.data?.dynamicsSyncEnabled ?? false;

    // État des tâches périodiques : la synchronisation Dynamics a sa propre cadence, et
    // savoir qu'elle tourne vaut mieux que de le supposer.
    const { data: schedulerData } = useQuery({
        queryKey: ['admin', 'scheduler'],
        queryFn: () => api.get<ApiResponse<SchedulerState>>('/api/sync/scheduler'),
        // La tâche passe toutes les cinq minutes : un rafraîchissement d'une minute suffit
        // à voir bouger le dernier passage sans interroger le serveur pour rien.
        refetchInterval: 60_000,
    });
    const dynamicsTask = schedulerData?.data?.dynamics;

    const logs = logsData?.data || [];

    const syncProjectsMutation = useMutation({
        mutationFn: () => api.post('/api/sync/projects'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sync-logs'] });
        },
    });

    const syncUsersMutation = useMutation({
        mutationFn: () => api.post('/api/sync/users'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sync-logs'] });
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });

    const syncTimeEntriesMutation = useMutation({
        mutationFn: () => api.post('/api/sync/time-entries'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sync-logs'] });
        },
    });

    const [dynamicsError, setDynamicsError] = useState<string | null>(null);
    const syncDynamicsMutation = useMutation({
        mutationFn: () => api.post('/api/sync/dynamics'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'sync-logs'] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setDynamicsError(null);
        },
        // Le message de Dataverse est la seule piste utile pour corriger un rôle ou
        // une URL : on l'affiche au lieu de le laisser dans les logs du serveur.
        onError: (err: Error) => setDynamicsError(err.message || 'Synchronisation impossible'),
    });

    return (
        <div className="space-y-4">
            <div className="card">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-medium text-[var(--color-text)]">Synchronisation SharePoint</h3>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            {sharepointEnabled
                                ? 'Synchronise les projets depuis la liste SharePoint'
                                : 'Désactivée dans l\'onglet Configuration'}
                        </p>
                    </div>
                    <button
                        onClick={() => syncProjectsMutation.mutate()}
                        disabled={syncProjectsMutation.isPending || !sharepointEnabled}
                        className="btn-primary"
                    >
                        {syncProjectsMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        Synchroniser les projets
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h3 className="font-medium text-[var(--color-text)]">Synchronisation Dynamics 365</h3>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            {dynamicsEnabled
                                ? 'Lit les commandes de la table salesorder : numéro de commande, client et jours vendus'
                                : 'Désactivée dans l\'onglet Configuration'}
                        </p>
                        {dynamicsTask && (
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                {dynamicsTask.scheduled
                                    ? `Tâche automatique ${describeCron(dynamicsTask.cronExpression)}`
                                    : 'Aucune tâche automatique programmée'}
                                {dynamicsTask.inProgress && ' · passage en cours'}
                                {dynamicsTask.lastRun && !dynamicsTask.inProgress && (
                                    <>
                                        {' · dernier passage '}
                                        {format(new Date(dynamicsTask.lastRun), 'dd/MM à HH:mm')}
                                        {dynamicsTask.lastResult &&
                                            ` (${dynamicsTask.lastResult.synced} commande(s), ` +
                                            `${dynamicsTask.lastResult.created} créé(s))`}
                                    </>
                                )}
                                {!dynamicsTask.lastRun && !dynamicsTask.inProgress && ' · aucun passage encore'}
                            </p>
                        )}
                        {dynamicsTask?.lastError && (
                            <p className="text-xs text-[var(--color-error)] mt-1 break-words">
                                Dernière erreur : {dynamicsTask.lastError}
                            </p>
                        )}
                        {dynamicsTask?.lastSkipped && !dynamicsTask.lastRun && (
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                Dernier passage sauté : {dynamicsTask.lastSkipped}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={() => syncDynamicsMutation.mutate()}
                        disabled={syncDynamicsMutation.isPending || !dynamicsEnabled}
                        className="btn-primary"
                    >
                        {syncDynamicsMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Database className="w-4 h-4" />
                        )}
                        Synchroniser les commandes
                    </button>
                </div>
                {dynamicsError && (
                    <p className="text-sm text-[var(--color-error)] mt-3 break-words">{dynamicsError}</p>
                )}
            </div>

            <div className="card">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-medium text-[var(--color-text)]">Synchronisation Entra ID</h3>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            Synchronise les utilisateurs depuis le groupe Entra ID de l'application
                        </p>
                    </div>
                    <button
                        onClick={() => syncUsersMutation.mutate()}
                        disabled={syncUsersMutation.isPending}
                        className="btn-primary"
                    >
                        {syncUsersMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        Synchroniser les utilisateurs
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-medium text-[var(--color-text)]">Migration Time Entries</h3>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            Importe les entrées de temps historiques depuis la liste SharePoint "TimeEntries"
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            if (confirm('Voulez-vous importer les time entries historiques depuis SharePoint? Les entrées existantes seront ignorées.')) {
                                syncTimeEntriesMutation.mutate();
                            }
                        }}
                        disabled={syncTimeEntriesMutation.isPending}
                        className="btn-primary bg-orange-600 hover:bg-orange-700"
                    >
                        {syncTimeEntriesMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        Importer Time Entries
                    </button>
                </div>
            </div>

            <DuplicateProjectsCard />

            <div className="card !p-0 overflow-hidden">
                <div className="p-4 border-b border-[var(--color-border)]">
                    <h3 className="font-medium text-[var(--color-text)]">Historique des synchronisations</h3>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-[var(--color-text-secondary)]">Aucun historique</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px]">
                            <thead className="bg-[var(--color-bg)]">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Éléments
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                        Durée
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--color-border)]">
                                {logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-[var(--color-bg)]">
                                        <td className="px-4 py-3 text-sm text-[var(--color-text)]">
                                            {format(new Date(log.startedAt), 'dd/MM/yyyy HH:mm')}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">{log.syncType}</td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={cn(
                                                    'flex items-center gap-1 text-sm',
                                                    log.status === 'success' && 'text-[var(--color-success)]',
                                                    log.status === 'error' && 'text-[var(--color-error)]',
                                                    log.status === 'running' && 'text-[var(--color-warning)]'
                                                )}
                                            >
                                                {log.status === 'success' && <Check className="w-4 h-4" />}
                                                {log.status === 'error' && <X className="w-4 h-4" />}
                                                {log.status === 'running' && <Loader2 className="w-4 h-4 animate-spin" />}
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text)]">{log.itemsSynced}</td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
                                            {log.completedAt
                                                ? `${Math.round(
                                                    (new Date(log.completedAt).getTime() -
                                                        new Date(log.startedAt).getTime()) /
                                                    1000
                                                )}s`
                                                : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

interface DuplicateProject {
    id: string;
    code: string;
    name: string;
    source: string | null;
    orderNumber: string | null;
    sharepointId: string | null;
    dynamicsId: string | null;
    timeEntries: number;
    members: number;
    cdps: number;
    keep: boolean;
}

interface DuplicateGroup {
    key: string;
    reason: 'order_number' | 'client_and_name';
    /** Vrai quand le rapprochement ne laisse pas de place au doute. */
    certain: boolean;
    label: string;
    projects: DuplicateProject[];
}

/**
 * Projets qui décrivent vraisemblablement la même commande.
 *
 * Le cas vient de la période où le numéro de commande n'était pas lu dans la liste
 * SharePoint : la synchronisation Dynamics ne pouvait pas reconnaître le projet et créait
 * sa propre ligne. Les deux synchronisations se rapprochent maintenant par ce numéro,
 * donc il ne s'en crée plus, mais les lignes déjà en double restent à réconcilier.
 *
 * Deux natures de rapprochement. Par numéro de commande, il est certain. Par client et
 * nom, il est probable : c'est le seul recours pour les projets dont la ligne SharePoint
 * n'a pas de numéro de commande, et il demande une confirmation une par une.
 */
function DuplicateProjectsCard() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<string | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['admin', 'duplicates'],
        queryFn: () => api.get<ApiResponse<DuplicateGroup[]>>('/api/sync/duplicates'),
    });

    const mergeMutation = useMutation({
        mutationFn: (key?: string) =>
            api.post<ApiResponse<{ merged: number; groups: number; errors: string[] }>>(
                '/api/sync/duplicates/merge',
                key ? { key } : {}
            ),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'duplicates'] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            const errors = response.data?.errors ?? [];
            setError(errors.length > 0 ? errors.join(' | ') : null);
            setDone(response.message ?? null);
        },
        onError: (err: Error) => {
            setError(err.message || 'Réconciliation impossible');
            setDone(null);
        },
    });

    const groups = data?.data ?? [];
    const certain = groups.filter((g) => g.certain);

    // Carte masquée quand tout est propre : elle ne raconte alors rien d'utile.
    if (isLoading || groups.length === 0) return null;

    return (
        <div className="card !p-0 overflow-hidden border-[var(--color-warning)]">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[var(--color-warning)]" />
                            <h3 className="font-medium text-[var(--color-text)]">
                                {groups.length} projet(s) en double
                                {certain.length !== groups.length &&
                                    ` · ${certain.length} certain(s), ${groups.length - certain.length} à confirmer`}
                            </h3>
                        </div>
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                            La ligne conservée est celle qui porte le plus de saisies de temps. Les autres
                            lui sont repliées : saisies, consultants, CDP, lignes de commande et
                            identifiants SharePoint comme Dynamics. Les lignes repliées sont supprimées.
                        </p>
                    </div>
                    <button
                        onClick={() => mergeMutation.mutate(undefined)}
                        disabled={mergeMutation.isPending || certain.length === 0}
                        className="btn-primary"
                        title="Ne traite que les rapprochements par numéro de commande"
                    >
                        {mergeMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Merge className="w-4 h-4" />
                        )}
                        Réconcilier les {certain.length} certain(s)
                    </button>
                </div>
                {error && <p className="text-sm text-[var(--color-error)] mt-3 break-words">{error}</p>}
                {done && !error && <p className="text-sm text-[var(--color-success)] mt-3">{done}</p>}
            </div>

            <div className="divide-y divide-[var(--color-border)]">
                {groups.map((group) => (
                    <div key={group.key} className="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <span
                                    className={cn(
                                        'px-2 py-0.5 rounded text-xs flex-shrink-0',
                                        group.certain
                                            ? 'bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]'
                                            : 'bg-[var(--color-warning)] text-white'
                                    )}
                                >
                                    {group.certain ? 'certain' : 'à confirmer'}
                                </span>
                                <p className="font-medium text-[var(--color-text)] truncate">{group.label}</p>
                            </div>
                            <button
                                onClick={() => mergeMutation.mutate(group.key)}
                                disabled={mergeMutation.isPending}
                                className="btn-secondary"
                            >
                                <Merge className="w-4 h-4" />
                                Réconcilier
                            </button>
                        </div>
                        {!group.certain && (
                            <p className="text-xs text-[var(--color-text-muted)] mb-2">
                                Rapprochés par leur client et leur nom, la ligne SharePoint n'ayant pas de
                                numéro de commande. À vérifier avant de fusionner.
                            </p>
                        )}
                        <div className="space-y-1">
                            {group.projects.map((project) => (
                                <div
                                    key={project.id}
                                    className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text-secondary)]"
                                >
                                    <span
                                        className={cn(
                                            'px-2 py-0.5 rounded text-xs',
                                            project.keep
                                                ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                                                : 'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]'
                                        )}
                                    >
                                        {project.keep ? 'conservé' : 'replié'}
                                    </span>
                                    <code className="text-[var(--color-text)]">{project.code}</code>
                                    <span className="truncate">{project.name}</span>
                                    <span className="text-[var(--color-text-muted)]">
                                        {project.source ?? 'manual'} ·{' '}
                                        {project.timeEntries} saisie(s) · {project.members} consultant(s)
                                        {project.cdps > 0 && ` · ${project.cdps} CDP`}
                                        {project.orderNumber && ` · ${project.orderNumber}`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Interrupteur réutilisé par les cartes de configuration.
function Toggle({
    checked,
    onChange,
    label,
    description,
}: {
    checked: boolean;
    onChange: (value: boolean) => void;
    label: string;
    description?: string;
}) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div>
                <p className="font-medium text-[var(--color-text)]">{label}</p>
                {description && (
                    <p className="text-sm text-[var(--color-text-secondary)]">{description}</p>
                )}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                    checked ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-bg-hover)]'
                )}
            >
                <span
                    className={cn(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
                        checked ? 'translate-x-5' : 'translate-x-0'
                    )}
                />
            </button>
        </div>
    );
}

interface AppSettings {
    sharepointSiteUrl: string;
    sharepointListName: string;
    syncIntervalMinutes: number;
    autoSyncEnabled: boolean;
    defaultWorkHoursPerDay: number;
    defaultWorkDaysPerWeek: number;
    sharepointSyncEnabled: boolean;
    dynamicsSyncEnabled: boolean;
    dynamicsOrgUrl: string;
    dynamicsServiceTypeField: string;
    dynamicsServiceTypeValue: string;
    /** Faux quand la ligne de réglages est introuvable : rien ne serait conservé. */
    persisted?: boolean;
}

interface DynamicsTest {
    orgUrl: string;
    clientId: string;
    credentialSource: string;
    userId?: string;
    organizationId?: string;
    orderCount: number;
    filterApplied: string | null;
    sample: {
        orderNumber: string | null;
        name: string | null;
        customer: string | null;
        soldDays: number | null;
    } | null;
}

interface DynamicsField {
    logicalName: string;
    displayName: string | null;
    type: string | null;
    isCustom: boolean;
    options?: Array<{ value: number; label: string | null }>;
}

// Settings Tab
function SettingsTab() {
    const api = useApi();
    const queryClient = useQueryClient();

    const { data: settingsData, isLoading } = useQuery({
        queryKey: ['admin', 'settings'],
        queryFn: () => api.get<ApiResponse<AppSettings>>('/api/settings'),
    });

    const [settings, setSettings] = useState<AppSettings | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Synchronisation de l'état local depuis la requête, pendant le rendu, comme le
    // recommande React plutôt qu'un effet qui provoquerait un second rendu. La garde
    // porte sur la RÉFÉRENCE des données reçues : TanStack Query la conserve tant que
    // le contenu ne change pas, si bien que la mise à jour n'a lieu qu'une fois par
    // réponse nouvelle. C'est ce qui manquait à une version plus ancienne, qui
    // appelait setSettings à chaque rendu et bouclait si l'URL SharePoint était vide.
    const [settingsSource, setSettingsSource] = useState<AppSettings | null>(null);
    if (settingsData?.data && settingsData.data !== settingsSource) {
        setSettingsSource(settingsData.data);
        setSettings(settingsData.data);
    }

    const saveMutation = useMutation({
        mutationFn: (data: AppSettings) => api.put<ApiResponse<AppSettings>>('/api/settings', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
            setSaveError(null);
        },
        onError: (err: Error) => setSaveError(err.message || "Échec de l'enregistrement"),
    });

    if (isLoading || !settings) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    const update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) =>
        setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));

    return (
        <div className="space-y-6">
            {settings.persisted === false && (
                <div className="card flex items-start gap-3 border-[var(--color-warning)]">
                    <AlertTriangle className="w-5 h-5 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-[var(--color-text)]">
                            Les réglages ne sont pas encore stockés en base
                        </p>
                        <p className="text-[var(--color-text-secondary)]">
                            Les valeurs affichées viennent des variables d'environnement. Appliquer la
                            migration <code>database/018_dynamics_integration.sql</code> pour pouvoir les
                            enregistrer.
                        </p>
                    </div>
                </div>
            )}

            {/* SharePoint */}
            <div className="card !p-0 overflow-hidden">
                <div className="p-4 border-b border-[var(--color-border)]">
                    <h3 className="font-medium text-[var(--color-text)]">Paramètres SharePoint</h3>
                </div>
                <div className="p-4 space-y-4">
                    <Toggle
                        checked={settings.sharepointSyncEnabled}
                        onChange={(v) => update('sharepointSyncEnabled', v)}
                        label="Synchronisation des projets depuis SharePoint"
                        description="Historiquement la seule source de projets. Peut être coupée une fois Dynamics en place."
                    />

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            URL du site SharePoint
                        </label>
                        <input
                            type="url"
                            value={settings.sharepointSiteUrl}
                            onChange={(e) => update('sharepointSiteUrl', e.target.value)}
                            placeholder="https://contoso.sharepoint.com/sites/projects"
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Nom de la liste
                        </label>
                        <input
                            type="text"
                            value={settings.sharepointListName}
                            onChange={(e) => update('sharepointListName', e.target.value)}
                            className="input"
                        />
                    </div>
                </div>
            </div>

            {/* Dynamics 365 */}
            <DynamicsSettingsCard settings={settings} update={update} />

            {/* Ordonnancement */}
            <div className="card !p-0 overflow-hidden">
                <div className="p-4 border-b border-[var(--color-border)]">
                    <h3 className="font-medium text-[var(--color-text)]">Synchronisation automatique</h3>
                </div>
                <div className="p-4 space-y-4">
                    <Toggle
                        checked={settings.autoSyncEnabled}
                        onChange={(v) => update('autoSyncEnabled', v)}
                        label="Synchronisation périodique"
                        description="Coupe d'un coup toutes les synchronisations programmées, sans toucher aux boutons manuels."
                    />

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Intervalle de synchronisation (minutes)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="1440"
                            value={settings.syncIntervalMinutes}
                            onChange={(e) => update('syncIntervalMinutes', parseInt(e.target.value, 10) || 1)}
                            className="input w-32"
                        />
                        <p className="text-xs text-[var(--color-text-muted)] mt-1">
                            La cadence réellement appliquée vient de <code>SYNC_CRON</code> ou
                            <code> SYNC_INTERVAL_MINUTES</code> côté serveur : reprogrammer la tâche en cours
                            de route demanderait un redémarrage.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                {saveError && <p className="text-sm text-[var(--color-error)]">{saveError}</p>}
                {saveMutation.isSuccess && !saveError && (
                    <p className="text-sm text-[var(--color-success)]">Réglages enregistrés</p>
                )}
                <button
                    onClick={() => saveMutation.mutate(settings)}
                    disabled={saveMutation.isPending}
                    className="btn-primary"
                >
                    {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enregistrer les paramètres
                </button>
            </div>
        </div>
    );
}

/**
 * Configuration de Dynamics 365.
 *
 * Le champ « Type de service » est une colonne personnalisée : son nom logique
 * dépend de l'organisation. Le bouton « Champs disponibles » interroge les
 * métadonnées de `salesorder` pour l'afficher, avec la valeur de chaque choix.
 */
function DynamicsSettingsCard({
    settings,
    update,
}: {
    settings: AppSettings;
    update: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
}) {
    const api = useApi();
    const [test, setTest] = useState<DynamicsTest | null>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [fields, setFields] = useState<DynamicsField[] | null>(null);

    const testMutation = useMutation({
        mutationFn: () => api.get<ApiResponse<DynamicsTest>>('/api/sync/dynamics/test'),
        onSuccess: (res) => {
            setTest(res.data);
            setTestError(null);
        },
        onError: (err: Error) => {
            setTest(null);
            setTestError(err.message || 'Connexion impossible');
        },
    });

    // Valeurs actuellement retenues, découpées exactement comme le serveur le fera.
    const selectedServiceTypes = parseServiceTypeValues(settings.dynamicsServiceTypeValue);

    // Libellés des options, quand la liste des champs a été chargée : « 464980001 » ne
    // dit rien, « Prestation » se relit.
    const serviceTypeLabels = new Map<string, string>(
        (fields || [])
            .filter((f) => f.logicalName === settings.dynamicsServiceTypeField)
            .flatMap((f) => f.options || [])
            .map((o) => [String(o.value), o.label || ''])
    );

    /**
     * Coche ou décoche une valeur. Changer de champ remet la liste à zéro : les valeurs
     * d'une liste de choix n'ont aucun sens pour une autre colonne.
     */
    const toggleServiceType = (fieldName: string, value: string) => {
        const sameField = settings.dynamicsServiceTypeField === fieldName;
        const next = toggleServiceTypeValue(sameField ? selectedServiceTypes : [], value);

        update('dynamicsServiceTypeField', fieldName);
        update('dynamicsServiceTypeValue', next.join(','));
    };

    const fieldsMutation = useMutation({
        mutationFn: () => api.get<ApiResponse<DynamicsField[]>>('/api/sync/dynamics/fields'),
        onSuccess: (res) => {
            setFields(res.data);
            setTestError(null);
        },
        onError: (err: Error) => {
            setFields(null);
            setTestError(err.message || 'Champs illisibles');
        },
    });

    return (
        <div className="card !p-0 overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <h3 className="font-medium text-[var(--color-text)]">Dynamics 365</h3>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    Lecture directe des commandes (table <code>salesorder</code>) : numéro de commande,
                    client et jours vendus. L'affectation des consultants reste gérée dans le CRA.
                </p>
            </div>

            <div className="p-4 space-y-4">
                <Toggle
                    checked={settings.dynamicsSyncEnabled}
                    onChange={(v) => update('dynamicsSyncEnabled', v)}
                    label="Synchronisation des projets depuis Dynamics"
                    description="Demande une organisation renseignée ci-dessous."
                />

                <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                        Organisation Dynamics
                    </label>
                    <input
                        type="url"
                        value={settings.dynamicsOrgUrl}
                        onChange={(e) => update('dynamicsOrgUrl', e.target.value)}
                        placeholder="https://contoso.crm4.dynamics.com"
                        className="input"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        L'URL de l'environnement, sans chemin. Elle sert aussi de portée au jeton.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Nom logique du champ « Type de service »
                        </label>
                        <input
                            type="text"
                            value={settings.dynamicsServiceTypeField}
                            onChange={(e) => update('dynamicsServiceTypeField', e.target.value)}
                            placeholder="ex. new_typedeservice"
                            className="input"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Valeurs retenues
                        </label>
                        <input
                            type="text"
                            value={settings.dynamicsServiceTypeValue}
                            onChange={(e) => update('dynamicsServiceTypeValue', e.target.value)}
                            placeholder="ex. 464980001,464980004"
                            className="input"
                        />
                    </div>
                </div>
                {selectedServiceTypes.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-[var(--color-text-muted)]">
                            {selectedServiceTypes.length === 1 ? 'Type retenu :' : 'Types retenus :'}
                        </span>
                        {selectedServiceTypes.map((value) => (
                            <span
                                key={value}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                            >
                                {serviceTypeLabels.get(value) || value}
                                <button
                                    type="button"
                                    onClick={() =>
                                        update(
                                            'dynamicsServiceTypeValue',
                                            selectedServiceTypes.filter((v) => v !== value).join(',')
                                        )
                                    }
                                    className="hover:text-[var(--color-error)]"
                                    title="Retirer cette valeur"
                                    aria-label={`Retirer ${value}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <p className="text-xs text-[var(--color-text-muted)]">
                    Les deux champs vides, toutes les commandes sont lues. Renseignés, seules celles dont
                    le type de service correspond le sont, quel que soit le nombre de types retenus. Le
                    premier champ attend le nom logique de la colonne, pas son libellé : le bouton
                    « Champs disponibles » le donne, et les valeurs s'y cochent d'un clic. À la main,
                    elles se séparent par des virgules.
                </p>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => testMutation.mutate()}
                        disabled={testMutation.isPending || !settings.dynamicsOrgUrl}
                        className="btn-secondary"
                    >
                        {testMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Link2 className="w-4 h-4" />
                        )}
                        Tester la connexion
                    </button>
                    <button
                        type="button"
                        onClick={() => fieldsMutation.mutate()}
                        disabled={fieldsMutation.isPending || !settings.dynamicsOrgUrl}
                        className="btn-secondary"
                    >
                        {fieldsMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Search className="w-4 h-4" />
                        )}
                        Champs disponibles
                    </button>
                </div>

                {/* Le test s'exécute sur les réglages enregistrés, pas sur la saisie en cours. */}
                <p className="text-xs text-[var(--color-text-muted)]">
                    Ces deux boutons utilisent les réglages déjà enregistrés : penser à enregistrer avant
                    de tester une nouvelle organisation.
                </p>

                {testError && (
                    <div className="flex items-start gap-2 text-sm text-[var(--color-error)]">
                        <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <p className="break-words">{testError}</p>
                    </div>
                )}

                {test && (
                    <div className="rounded-lg bg-[var(--color-bg)] p-3 text-sm space-y-1">
                        <p className="flex items-center gap-2 text-[var(--color-success)]">
                            <Check className="w-4 h-4" />
                            Connexion établie sur {test.orgUrl}
                        </p>
                        <p className="text-[var(--color-text-secondary)]">
                            {test.orderCount} commande(s) correspondent
                            {test.filterApplied ? ` au filtre ${test.filterApplied}` : ' (aucun filtre)'}.
                        </p>
                        {/* L'application utilisée est affichée : c'est la première chose à
                            vérifier quand Entra ID refuse le jeton. */}
                        <p className="text-[var(--color-text-muted)]">
                            Application {test.clientId}, issue de {test.credentialSource}.
                        </p>
                        {test.sample && (
                            <p className="text-[var(--color-text-muted)]">
                                Exemple : {test.sample.orderNumber || 'sans numéro'} — {test.sample.name || 'sans nom'}
                                {test.sample.customer && ` — ${test.sample.customer}`}
                                {test.sample.soldDays !== null && ` — ${test.sample.soldDays}j`}
                            </p>
                        )}
                    </div>
                )}

                {fields && (
                    <div className="rounded-lg bg-[var(--color-bg)] p-3 text-sm">
                        <p className="font-medium text-[var(--color-text)] mb-2">
                            {fields.length} colonne(s) personnalisée(s) sur salesorder
                        </p>
                        {fields.length === 0 ? (
                            <p className="text-[var(--color-text-muted)]">
                                Aucune colonne personnalisée trouvée.
                            </p>
                        ) : (
                            <ul className="space-y-2 max-h-72 overflow-y-auto">
                                {fields.map((f) => (
                                    <li key={f.logicalName}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                // Changer de colonne rend les valeurs retenues
                                                // caduques : on repart de zéro.
                                                if (settings.dynamicsServiceTypeField !== f.logicalName) {
                                                    update('dynamicsServiceTypeValue', '');
                                                }
                                                update('dynamicsServiceTypeField', f.logicalName);
                                            }}
                                            className="text-left text-[var(--color-primary)] hover:underline"
                                            title="Utiliser ce champ comme type de service"
                                        >
                                            <code>{f.logicalName}</code>
                                        </button>
                                        <span className="text-[var(--color-text-muted)]">
                                            {f.displayName ? ` — ${f.displayName}` : ''} ({f.type || 'type inconnu'})
                                        </span>
                                        {f.options && f.options.length > 0 && (
                                            <span className="flex flex-wrap gap-1 text-xs pl-4 mt-1">
                                                {f.options.map((o) => {
                                                    const selected =
                                                        settings.dynamicsServiceTypeField === f.logicalName &&
                                                        selectedServiceTypes.includes(String(o.value));
                                                    return (
                                                        <button
                                                            key={o.value}
                                                            type="button"
                                                            onClick={() => toggleServiceType(f.logicalName, String(o.value))}
                                                            className={cn(
                                                                'px-2 py-0.5 rounded border transition-colors',
                                                                selected
                                                                    ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white'
                                                                    : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-card)]'
                                                            )}
                                                            title={selected ? 'Retirer cette valeur' : 'Retenir cette valeur'}
                                                        >
                                                            {selected && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                                                            {o.value} = {o.label || '(sans libellé)'}
                                                        </button>
                                                    );
                                                })}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
